import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ATTENDANCE_STATUSES,
  cellKey,
  type AttendanceStatus,
  type GridMark,
  type GridResponse,
} from '@quire/shared';
import { ApiError, api } from '../lib/api';
import { applySaved, settleDraft } from '../lib/autosave';
import { ErrorNote, Spinner } from '../components/ui';

/**
 * A mark is a letterform, not a coloured pill: it survives printing and
 * photocopying, it never carries meaning in colour alone, and a column of
 * aligned monospace glyphs is far quicker to scan than a column of badges.
 */
const STATUS_META: Record<AttendanceStatus, { glyph: string; label: string; key: string }> = {
  present: { glyph: 'P', label: 'Present', key: 'p' },
  absent: { glyph: 'A', label: 'Absent', key: 'a' },
  late: { glyph: 'L', label: 'Late', key: 'l' },
  excused: { glyph: 'E', label: 'Excused', key: 'e' },
};

/**
 * Clicking only ever walks between the two marks a register is mostly made of.
 *
 * Late and excused are the exceptions, and putting them in the click cycle
 * charged every ordinary mark for them: correcting one mis-click meant tapping
 * through four states to get back. They are still set outright from the
 * keyboard, along with clearing a cell.
 *
 * A cell that is unmarked, late or excused is not in this list, so `indexOf`
 * returns -1 and the first click lands on `present`. That is the intended
 * behaviour rather than an accident of the arithmetic: a click always moves a
 * cell into the ordinary pair.
 */
const CYCLE: AttendanceStatus[] = ['present', 'absent'];

/**
 * How long after the last mark a save goes out.
 *
 * Long enough that marking a column is one request, short enough that a laptop
 * closed straight after a click loses nothing worth naming. The flush points
 * cover the rest.
 */
const AUTOSAVE_DELAY = 1000;

/**
 * A finger held on a cell opens the same menu a right click does.
 *
 * Half a second is what both phone platforms use for their own long presses,
 * so it is what a thumb already expects. The slop is how far the finger may
 * drift and still count as holding still; further than that and it is the
 * start of a scroll, which must never open a menu.
 */
const HOLD_DELAY = 500;
const HOLD_SLOP = 10;

function shiftDate(date: string, days: number): string {
  const instant = new Date(`${date}T00:00:00Z`);
  instant.setUTCDate(instant.getUTCDate() + days);
  return instant.toISOString().slice(0, 10);
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}

export function Grid() {
  const { classId } = useParams({ from: '/app/classes/$classId' });
  const search = useSearch({ from: '/app/classes/$classId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const today = new Date().toISOString().slice(0, 10);
  const date = search.date ?? today;

  const [draft, setDraft] = useState<Record<string, AttendanceStatus | null>>({});
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [menu, setMenu] = useState<MarkMenuTarget | null>(null);

  /*
   * The hold in progress, if any, and the cell whose next click is the finger
   * coming back up after one. Refs rather than state: a hold changes nothing
   * on screen until it fires, and a grid is easily several hundred cells.
   *
   * `swallowClick` exists because lifting the finger that opened the menu can
   * be followed by a click, which would toggle the cell present under the
   * menu that has just opened for it. It is cleared by the next pointer down
   * and when the menu closes, so a stale value can never eat a later tap.
   */
  const hold = useRef<{ timer: number; cellId: string; x: number; y: number } | null>(null);
  const swallowClick = useRef<string | null>(null);

  const cancelHold = useCallback(() => {
    if (hold.current) {
      window.clearTimeout(hold.current.timer);
      hold.current = null;
    }
  }, []);
  useEffect(() => cancelHold, [cancelHold]);

  const [focus, setFocus] = useState<{ row: number; col: number } | null>(null);
  const cellRefs = useRef(new Map<string, HTMLButtonElement>());

  const grid = useQuery({
    queryKey: ['grid', classId, date],
    queryFn: () => api.classes.grid(classId, date),
  });

  // Changing class or date clears the draft: an unsaved edit belongs to the day
  // it was made on. This adjusts state during render rather than in an effect -
  // React's documented pattern for resetting state when a prop changes, which
  // avoids the extra render pass an effect would cause.
  const gridKey = `${classId}:${date}`;
  const [lastGridKey, setLastGridKey] = useState(gridKey);
  if (lastGridKey !== gridKey) {
    setLastGridKey(gridKey);
    setDraft({});
    setFocus(null);
    setLastSavedAt(null);
  }

  const save = useMutation({
    mutationFn: (marks: GridMark[]) => api.classes.saveGrid(classId, { date, marks }),
    /*
     * A 4xx means the request itself is wrong, a student no longer enrolled on
     * this date being the likely one, and sending it again cannot help.
     * Anything else gets two more attempts, because the common failure while
     * marking a register is a school's wifi rather than the payload.
     */
    retry: (attempt, error) =>
      attempt < 3 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
    onSuccess: async (_result, sent) => {
      /*
       * The cache patch and the draft clear are one commit on purpose.
       * `statusAt` reads the draft first and falls back to these cells, so
       * taking a key out of the draft without writing its value here makes the
       * cell visibly flick back to what it held before, for as long as the
       * refetch takes.
       *
       * `settleDraft` rather than `setDraft({})`: a cell can be marked again
       * while the request carrying its previous value is still in the air, and
       * that newer mark has been sent nowhere yet. It has to survive.
       */
      queryClient.setQueryData<GridResponse>(['grid', classId, date], (prev) =>
        prev ? { ...prev, cells: applySaved(prev.cells, sent) } : prev,
      );
      setDraft((current) => settleDraft(current, sent));
      setLastSavedAt(new Date());
      await queryClient.invalidateQueries({ queryKey: ['grid', classId, date] });
    },
  });

  const data = grid.data;

  const statusAt = useCallback(
    (studentId: string, periodId: string): AttendanceStatus | null => {
      const key = cellKey(studentId, periodId);
      if (key in draft) return draft[key] ?? null;
      return data?.cells[key] ?? null;
    },
    [draft, data],
  );

  const setStatus = useCallback(
    (studentId: string, periodId: string, status: AttendanceStatus | null) => {
      const key = cellKey(studentId, periodId);
      setDraft((current) => {
        const next = { ...current };
        const saved = data?.cells[key] ?? null;
        // Returning a cell to its saved value removes it from the draft, so the
        // "unsaved" count only ever reflects genuine differences.
        if (saved === status) delete next[key];
        else next[key] = status;
        return next;
      });
    },
    [data],
  );

  const pending = useMemo(
    () =>
      Object.entries(draft).map(([key, status]) => {
        const [studentId, periodId] = key.split(':') as [string, string];
        return { studentId, periodId, status };
      }),
    [draft],
  );

  const canEdit = data?.canEdit ?? false;
  const { mutate: runSave, isPending: saving, isError: failed, failureCount } = save;

  /*
   * One save in flight at a time, ever, and the debounce falls out of that.
   *
   * `pending` is rebuilt whenever the draft changes, so this re-runs on every
   * mark and restarts the timer: a run of clicks down a column leaves as one
   * request. Nothing is scheduled while a save is in the air; when one settles
   * this runs again and picks up whatever arrived meanwhile. That is what stops
   * two requests racing and landing out of order, which would leave the server
   * holding the earlier of two marks.
   *
   * `failed` gates it off completely. Once the retries are spent, continuing to
   * mark must not fire a doomed request per click: the banner says what
   * happened, the dots say which cells are outstanding, and Retry is the way
   * back.
   */
  useEffect(() => {
    if (!canEdit || pending.length === 0 || saving || failed) return;
    const timer = window.setTimeout(() => runSave(pending), AUTOSAVE_DELAY);
    return () => window.clearTimeout(timer);
  }, [canEdit, pending, saving, failed, runSave]);

  /*
   * Closing a laptop or switching tabs is where the debounce window actually
   * costs something, so hidden means flush now.
   *
   * Deliberately outside the mutation: the page is going away, so there is
   * nobody left to show a result to and no state worth updating. The server
   * diffs every mark, so a flush that overlaps the timer is at worst a
   * redundant no-op rather than a conflict.
   */
  const outstanding = useRef(pending);
  useEffect(() => {
    outstanding.current = pending;
  }, [pending]);

  useEffect(() => {
    if (!canEdit) return;
    const flush = () => {
      if (document.visibilityState !== 'hidden' || outstanding.current.length === 0) return;
      void api.classes
        .saveGrid(classId, { date, marks: outstanding.current }, { keepalive: true })
        .catch(() => {
          /* The page is unloading. There is nowhere to report this. */
        });
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [canEdit, classId, date]);

  /*
   * The one case the flushes cannot cover: a save that has genuinely failed and
   * is waiting on Retry. Leaving now, in the app or by closing the tab, loses
   * those marks, so both are worth interrupting. `disabled` keeps it inert the
   * rest of the time, which is almost always.
   */
  useBlocker({
    shouldBlockFn: () =>
      !window.confirm(
        'Some marks on this register have not been saved. Leave anyway and lose them?',
      ),
    enableBeforeUnload: true,
    disabled: !failed || pending.length === 0,
  });

  const moveFocus = useCallback(
    (rowDelta: number, colDelta: number) => {
      if (!data || !focus) return;
      const row = Math.min(Math.max(focus.row + rowDelta, 0), data.students.length - 1);
      const col = Math.min(Math.max(focus.col + colDelta, 0), data.periods.length - 1);
      setFocus({ row, col });
      const student = data.students[row];
      const period = data.periods[col];
      if (student && period) {
        cellRefs.current.get(cellKey(student.id, period.id))?.focus();
      }
    },
    [data, focus],
  );

  if (grid.isLoading) return <Spinner label="Opening register" />;
  if (grid.error) {
    const error = grid.error;
    return (
      <ErrorNote
        message={
          error instanceof ApiError && error.status === 403
            ? 'You are not assigned to this class.'
            : error instanceof ApiError && error.status === 404
              ? 'That class does not exist.'
              : 'Could not load the register.'
        }
      />
    );
  }
  if (!data) return null;

  /*
   * Flush before moving. Changing the date resets the draft, so an unsaved mark
   * would simply vanish, and a promise to save by itself has to include saving
   * before the page changes underneath you.
   *
   * A failure keeps you on the date rather than moving on without the marks.
   * The banner and Retry are already on screen by then, so there is nothing
   * more to say here.
   */
  const goToDate = async (next: string) => {
    if (pending.length > 0) {
      try {
        await save.mutateAsync(pending);
      } catch {
        return;
      }
    }
    await navigate({ to: '/classes/$classId', params: { classId }, search: { date: next } });
  };

  return (
    <div>
      <GridHeader
        data={data}
        date={date}
        onDate={(next) => void goToDate(next)}
        onToday={() => void goToDate(today)}
        status={{
          unsaved: pending.length,
          saving,
          retrying: saving && failureCount > 0,
          failed,
          lastSavedAt,
          onRetry: () => runSave(pending),
        }}
      />

      {failed ? (
        <div className="mb-4">
          <ErrorNote
            message={`${
              save.error instanceof ApiError ? save.error.message : 'Could not save marks.'
            } Nothing has been lost: the cells still carrying a dot are the ones outstanding. Press Retry.`}
          />
        </div>
      ) : null}

      {data.students.length === 0 || data.periods.length === 0 ? (
        <div className="panel px-6 py-14 text-center">
          <p className="mb-1 font-semibold">
            {data.periods.length === 0 ? 'No periods yet' : 'No students enrolled'}
          </p>
          <p className="text-sm text-ink-soft">
            {data.periods.length === 0
              ? 'Add periods to this class under Classes before taking attendance.'
              : 'Enrol students in this class under Classes to start marking.'}
          </p>
        </div>
      ) : (
        /*
          relative, so that this scroller is the containing block for the
          sr-only column numbers in the header cells. They are absolutely
          positioned, and an overflow box only clips what it contains; without
          this they sit at the table's right edge, outside the viewport on a
          phone, and widen the page by that much without painting anything.
        */
        <div className="relative inline-block max-w-full overflow-x-auto align-top">
          <table className="border-collapse text-sm">
            {/*
              Tighter below `sm`. On a phone the student column gives up its
              wide right margin and lets a long name wrap, and each period
              column keeps only 6px either side of its 44px mark, so that
              three periods fit a 360px screen. A fourth scrolls behind the
              sticky name column, as a grid must.
            */}
            <thead>
              <tr className="border-b-2 border-rule-strong">
                {/* Opaque, not raised: the student column has to mask the marks
                    scrolling under it without becoming a band floating on the sheet. */}
                <th className="sticky left-0 z-10 bg-paper px-3 py-2.5 pr-4 text-left align-bottom sm:px-4 sm:pr-8">
                  <span className="eyebrow">Student</span>
                </th>
                {data.periods.map((period, col) => (
                  <th
                    key={period.id}
                    className="rule-y px-1.5 py-2.5 text-center align-bottom sm:px-3"
                  >
                    <span className="block text-[13px] font-semibold">{period.name}</span>
                    <span className="mt-0.5 block font-mono text-[11px] text-ink-faint">
                      {period.startTime ? period.startTime.slice(0, 5) : '-'}
                    </span>
                    {data.canEdit ? (
                      // Padding in place of the old top margin, so the same
                      // 10px label has a hit area a finger can find.
                      <button
                        type="button"
                        className="-mx-1 -mb-1.5 px-1 pt-1.5 pb-1.5 font-mono text-[10px] tracking-wider text-ink-faint uppercase underline underline-offset-2 hover:text-ink"
                        onClick={() => {
                          for (const student of data.students) {
                            setStatus(student.id, period.id, 'present');
                          }
                        }}
                      >
                        all present
                      </button>
                    ) : null}
                    <span className="sr-only">{`column ${col + 1}`}</span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {data.students.map((student, row) => (
                <tr key={student.id} className="border-t border-rule hover:bg-paper">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-paper px-3 py-1.5 pr-4 text-left font-normal sm:px-4 sm:pr-8 sm:whitespace-nowrap"
                  >
                    {/* The index is a gutter the name wraps beside, not a
                        word the name wraps under. */}
                    <span className="flex items-baseline gap-2.5">
                      <span className="font-mono text-[11px] text-ink-faint tabular">
                        {String(row + 1).padStart(2, '0')}
                      </span>
                      <span className="font-medium">
                        {student.lastName}, {student.firstName}
                      </span>
                    </span>
                  </th>

                  {data.periods.map((period, col) => {
                    const status = statusAt(student.id, period.id);
                    const key = cellKey(student.id, period.id);
                    const isDirty = key in draft;
                    const menuAt = (x: number, y: number): MarkMenuTarget => ({
                      x,
                      y,
                      studentId: student.id,
                      periodId: period.id,
                      cellId: key,
                      label: `${student.firstName} ${student.lastName}, ${period.name}`,
                      status,
                    });
                    return (
                      <td key={period.id} className="rule-y px-1.5 py-1 sm:px-3">
                        <button
                          type="button"
                          ref={(node) => {
                            if (node) cellRefs.current.set(key, node);
                            else cellRefs.current.delete(key);
                          }}
                          className="mark relative"
                          data-status={status ?? undefined}
                          data-focused={
                            focus?.row === row && focus?.col === col ? 'true' : undefined
                          }
                          disabled={!data.canEdit}
                          aria-label={`${student.firstName} ${student.lastName}, ${period.name}: ${
                            status ? STATUS_META[status].label : 'not marked'
                          }`}
                          onFocus={() => setFocus({ row, col })}
                          onPointerDown={(event) => {
                            cancelHold();
                            swallowClick.current = null;
                            // A mouse already has a right click, and a mouse user
                            // who hesitates on a click must not get a menu instead.
                            if (
                              !data.canEdit ||
                              event.pointerType === 'mouse' ||
                              !event.isPrimary
                            ) {
                              return;
                            }
                            const cellEl = event.currentTarget;
                            hold.current = {
                              cellId: key,
                              x: event.clientX,
                              y: event.clientY,
                              timer: window.setTimeout(() => {
                                hold.current = null;
                                swallowClick.current = key;
                                // The confirmation a phone's own long press gives.
                                // iOS has no vibration API; Android may refuse
                                // before the first completed tap. Neither matters.
                                navigator.vibrate?.(15);
                                // Hung off the cell rather than under the finger,
                                // which would be covering it.
                                const box = cellEl.getBoundingClientRect();
                                setMenu(menuAt(box.left, box.bottom));
                              }, HOLD_DELAY),
                            };
                          }}
                          onPointerMove={(event) => {
                            const current = hold.current;
                            if (
                              current?.cellId === key &&
                              Math.hypot(event.clientX - current.x, event.clientY - current.y) >
                                HOLD_SLOP
                            ) {
                              cancelHold();
                            }
                          }}
                          onPointerUp={cancelHold}
                          onPointerCancel={cancelHold}
                          onPointerLeave={cancelHold}
                          onContextMenu={(event) => {
                            if (!data.canEdit) return;
                            event.preventDefault();
                            // Android raises this from the same long press the
                            // hold above recognises. Whichever fires first opens
                            // the menu; the other must not open a second one.
                            if (swallowClick.current === key) return;
                            if (hold.current?.cellId === key) {
                              cancelHold();
                              swallowClick.current = key;
                            }
                            // The context menu key reports no pointer position,
                            // so anchor to the cell rather than to (0, 0).
                            const box = event.currentTarget.getBoundingClientRect();
                            const fromKeyboard = event.clientX === 0 && event.clientY === 0;
                            setMenu(
                              menuAt(
                                fromKeyboard ? box.left : event.clientX,
                                fromKeyboard ? box.bottom : event.clientY,
                              ),
                            );
                          }}
                          onClick={() => {
                            if (swallowClick.current === key) {
                              swallowClick.current = null;
                              return;
                            }
                            const index = status ? CYCLE.indexOf(status) : -1;
                            setStatus(student.id, period.id, CYCLE[(index + 1) % CYCLE.length]!);
                          }}
                          onKeyDown={(event) => {
                            const pressed = event.key.toLowerCase();
                            const match = (
                              Object.entries(STATUS_META) as [
                                AttendanceStatus,
                                (typeof STATUS_META)[AttendanceStatus],
                              ][]
                            ).find(([, meta]) => meta.key === pressed);

                            if (match) {
                              event.preventDefault();
                              setStatus(student.id, period.id, match[0]);
                              return;
                            }
                            if (pressed === 'backspace' || pressed === 'delete') {
                              event.preventDefault();
                              setStatus(student.id, period.id, null);
                              return;
                            }
                            const moves: Record<string, [number, number]> = {
                              arrowup: [-1, 0],
                              arrowdown: [1, 0],
                              arrowleft: [0, -1],
                              arrowright: [0, 1],
                            };
                            const move = moves[pressed];
                            if (move) {
                              event.preventDefault();
                              moveFocus(move[0], move[1]);
                            }
                          }}
                        >
                          {status ? STATUS_META[status].glyph : '·'}
                          {isDirty ? (
                            <span
                              aria-hidden
                              className="absolute top-1 right-1 size-1.5 rounded-full bg-ink"
                            />
                          ) : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {/* Running tallies, the way a paper register totals each column. */}
            <tfoot>
              <tr className="border-t-2 border-rule-strong">
                <td className="sticky left-0 bg-paper px-3 py-2.5 sm:px-4">
                  <span className="eyebrow">In attendance</span>
                </td>
                {data.periods.map((period) => {
                  const marks = data.students.map((s) => statusAt(s.id, period.id));
                  const here = marks.filter((m) => m === 'present' || m === 'late').length;
                  const marked = marks.filter((m) => m !== null).length;
                  return (
                    <td key={period.id} className="rule-y px-1.5 py-2.5 text-center sm:px-3">
                      <span className="font-mono text-[13px] font-semibold tabular">
                        {here}
                        <span className="text-ink-faint">/{data.students.length}</span>
                      </span>
                      {marked < data.students.length ? (
                        <span className="mt-0.5 block font-mono text-[10px] text-ink-faint">
                          {data.students.length - marked} unmarked
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Legend canEdit={data.canEdit} />

      {menu ? (
        <MarkMenu
          target={menu}
          onPick={(status) => {
            setStatus(menu.studentId, menu.periodId, status);
            setMenu(null);
            swallowClick.current = null;
            // Focus goes back where it came from, so the keyboard path does not
            // dump the caller at the top of the document.
            cellRefs.current.get(menu.cellId)?.focus();
          }}
          onClose={() => {
            setMenu(null);
            swallowClick.current = null;
            cellRefs.current.get(menu.cellId)?.focus();
          }}
        />
      ) : null}
    </div>
  );
}

/** Everything the status line needs to say what is true right now. */
interface SaveStatus {
  unsaved: number;
  saving: boolean;
  /** A retry is in flight, as opposed to a first attempt. */
  retrying: boolean;
  /** The retries are spent. Nothing will be sent until Retry is pressed. */
  failed: boolean;
  lastSavedAt: Date | null;
  onRetry: () => void;
}

function GridHeader({
  data,
  date,
  onDate,
  onToday,
  status,
}: {
  data: GridResponse;
  date: string;
  onDate: (next: string) => void;
  onToday: () => void;
  status: SaveStatus;
}) {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1.5">Attendance</p>
          <h1 className="text-[28px] leading-none font-semibold tracking-[-0.02em]">
            {data.class.name}
          </h1>
        </div>

        {/*
          flex-wrap, because the row is wider than a phone: the two week
          buttons, the date and Today come to about 420px against a 365px
          viewport. The container around it already wraps; only this row did
          not, so it squeezed its buttons until their labels spilled out.
        */}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn" onClick={() => onDate(shiftDate(date, -7))}>
            ‹ Previous week
          </button>
          <input
            type="date"
            className="field w-auto font-mono"
            value={date}
            onChange={(e) => e.target.value && onDate(e.target.value)}
          />
          <button type="button" className="btn" onClick={() => onDate(shiftDate(date, 7))}>
            Next week ›
          </button>
          <button type="button" className="btn" onClick={onToday}>
            Today
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-3">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[13px] tabular">{formatDate(date)}</span>
          {data.exceptionReason ? (
            <span
              className="rounded px-2 py-0.5 text-xs"
              style={{ background: 'var(--color-late-soft)', color: 'var(--color-late)' }}
            >
              Cancelled - {data.exceptionReason}
            </span>
          ) : !data.isScheduledDay ? (
            <span className="text-xs text-ink-soft">
              Not a scheduled meeting day. Marks still save.
            </span>
          ) : null}
        </div>

        {data.canEdit ? <SaveStatusLine status={status} /> : null}
      </div>
    </div>
  );
}

/**
 * What replaced the Save button.
 *
 * Marks save themselves, so a button that is disabled whenever there is nothing
 * to do and redundant whenever there is would be the most prominent control on
 * the page for no reason. One appears only when a save has actually failed,
 * which is the only moment there is something a person can do about it.
 *
 * The visible text is hidden from assistive technology and a quieter live
 * region carries only the settled outcomes. Announcing the visible text would
 * read out "1 unsaved, 2 unsaved, 3 unsaved, Saving, Saved" through a single
 * run down a column, which is noise standing exactly where the useful sentence
 * should be.
 */
function SaveStatusLine({ status }: { status: SaveStatus }) {
  const { unsaved, saving, retrying, failed, lastSavedAt, onRetry } = status;

  const visible = failed
    ? 'Not saved'
    : retrying
      ? 'Trying again…'
      : saving
        ? 'Saving…'
        : unsaved > 0
          ? `${unsaved} unsaved`
          : lastSavedAt
            ? `Saved ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : 'All changes saved';

  /*
   * Only the outcomes, so a burst of marking announces once at the end of it
   * rather than counting up through it.
   *
   * Blank while a save is in flight for two reasons. It stops the line reading
   * "Attendance saved" through a run of retries, which is the opposite of what
   * is happening, and it makes each completed save announce again: without the
   * blanking the text never changes after the first one, so a screen reader
   * hears nothing about any save but the first.
   */
  const announced = failed
    ? 'Attendance not saved'
    : saving
      ? ''
      : lastSavedAt
        ? 'Attendance saved'
        : '';

  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className={`font-mono text-xs tabular ${failed ? 'font-semibold text-absent' : 'text-ink-soft'}`}
      >
        {visible}
      </span>
      <span className="sr-only" role="status" aria-live="polite">
        {announced}
      </span>
      {failed ? (
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

function Legend({ canEdit }: { canEdit: boolean }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-rule pt-3">
      {(
        Object.entries(STATUS_META) as [AttendanceStatus, (typeof STATUS_META)[AttendanceStatus]][]
      ).map(([status, meta]) => (
        <span key={status} className="flex items-center gap-1.5">
          <span
            className="grid size-5 place-items-center rounded-[3px] font-mono text-[11px] font-semibold"
            style={{
              background: `var(--color-${status}-soft)`,
              color: `var(--color-${status})`,
            }}
          >
            {meta.glyph}
          </span>
          <span className="text-xs text-ink-soft">{meta.label}</span>
        </span>
      ))}
      {canEdit ? (
        /*
          Two sentences for two kinds of pointer. A finger has neither a right
          button nor arrow keys, and telling it about them is noise in the
          one line that explains the register; the hold that opens the menu
          (see the cell's pointer handlers) is what it needs to hear about.
        */
        <span className="font-mono text-[11px] text-ink-faint">
          <span className="pointer-coarse:hidden">
            click for present or absent · right click for all · P A L E to set · ← ↑ ↓ → to move
          </span>
          <span className="hidden pointer-coarse:inline">
            tap for present or absent · hold for all
          </span>
        </span>
      ) : null}
    </div>
  );
}

interface MarkMenuTarget {
  x: number;
  y: number;
  studentId: string;
  periodId: string;
  /** cellKey, so focus can be returned to the cell that opened the menu. */
  cellId: string;
  label: string;
  status: AttendanceStatus | null;
}

/**
 * The full set of marks, on a right click.
 *
 * Clicking a cell deliberately only alternates present and absent, which leaves
 * late, excused and clearing reachable from the keyboard alone. This is the way
 * to them without one, and it costs the ordinary mark nothing because it is not
 * in the ordinary mark's path.
 *
 * One menu for the whole register rather than one per cell: a grid is easily
 * several hundred cells, and each would otherwise carry its own listeners and
 * its own element.
 *
 * Opening it from the keyboard works too, since the context menu key and
 * Shift+F10 both raise `contextmenu`. Those report no pointer position, so the
 * caller anchors to the cell instead.
 */
function MarkMenu({
  target,
  onPick,
  onClose,
}: {
  target: MarkMenuTarget;
  onPick: (status: AttendanceStatus | null) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState({ left: target.x, top: target.y });

  // Measured rather than estimated, so a cell near the right or bottom edge
  // does not open a menu that runs off the page.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPlaced({
      left: Math.max(8, Math.min(target.x, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(target.y, window.innerHeight - height - 8)),
    });
    el.querySelector<HTMLButtonElement>('button')?.focus();
  }, [target.x, target.y]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    // Scrolling the register would leave the menu behind, pointing at nothing.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  const roam = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (at + (event.key === 'ArrowDown' ? 1 : items.length - 1) + items.length) % items.length;
    items[next]?.focus();
  };

  const options: { status: AttendanceStatus | null; glyph: string; label: string }[] = [
    ...ATTENDANCE_STATUSES.map((status) => ({
      status,
      glyph: STATUS_META[status].glyph,
      label: STATUS_META[status].label,
    })),
    { status: null, glyph: '·', label: 'Clear' },
  ];

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={`Mark ${target.label}`}
      className="popover fixed"
      style={{ left: placed.left, top: placed.top }}
      onKeyDown={roam}
    >
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          role="menuitem"
          className="menu-item"
          aria-current={option.status === target.status ? 'true' : undefined}
          onClick={() => onPick(option.status)}
        >
          <span className="mark-menu-glyph" data-status={option.status ?? undefined}>
            {option.glyph}
          </span>
          {option.label}
        </button>
      ))}
    </div>
  );
}
